import { authApi } from "auth/api/authApi";
import { clearAuthToken, getAuthToken, getAuthTokenDecoded, isAuthTokenExpired } from "auth/authToken";
import { AuthMode } from "auth/types";
import { getAuthMode } from "config/config";
import { useCallback, useMemo } from "react";
import { store } from "redux/store";

export const useAuth = () => {
    const authMode = getAuthMode();

    const isAuthenticated = useCallback((): boolean => {
        if (authMode === AuthMode.NONE) {
            return true;
        }

        const token = getAuthToken();
        return !!token && !isAuthTokenExpired(token);
    }, [authMode]);

    const getUserName = useCallback(() => {
        if (authMode === AuthMode.NONE) {
            return "";
        }

        const token = getAuthTokenDecoded();
        if (!token) {
            return "";
        }

        return token?.unique_name ?? token?.sub ?? "";
    }, [authMode]);

    const roles = useMemo(() => {
        const token = getAuthTokenDecoded();
        return new Set(token?.roles ?? []);
    }, []);

    const hasRole = useCallback(
        (role: string) => {
            if (authMode === AuthMode.NONE) {
                return true;
            }

            return roles.has(role);
        },
        [roles, authMode],
    );

    const signOut = useCallback(() => {
        if (authMode === AuthMode.NONE) {
            return;
        }

        // Fire-and-forget audit call; signing out must not depend on it succeeding.
        // Dispatched to the container store explicitly rather than via useLogoutMutation: the sign-out button lives in the
        // header, which remote apps render inside their own ReduxProvider, so the hook would pick up the remote's store
        // (which has no authApi middleware) and throw before the token is cleared.
        const token = getAuthToken();
        if (token && !isAuthTokenExpired(token)) {
            store.dispatch(authApi.endpoints.logout.initiate(token));
        }

        clearAuthToken();
    }, [authMode]);

    return { isAuthenticated, getUserName, signOut, hasRole };
};
